import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Combobox } from "../components/ui/combobox";
import { PersonSelect, PersonOption } from "../components/ui/person-select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { validatePassword, defaultRequirements } from "../utils/password-validation";
import { PasswordStrengthIndicator } from "../components/PasswordStrengthIndicator";
import { PasswordRequirements } from "../components/PasswordRequirements";
import { JobRoleTitle, UserRole, User } from "../types/index";
import type { SubcontractorVendorOption } from "../types/lists.types";
import api from "../utils/api";
import { EMERGENCY_CONTACT_RELATIONSHIP_OPTIONS } from "../constants/person.constants";
import {
  GENDER_OPTIONS,
  INDIGENOUS_STATUS_OPTIONS,
  COUNTRY_OF_BIRTH_OPTIONS,
  DISABILITY_STATUS_OPTIONS,
  WORK_LOCATION_OPTIONS,
} from "../constants/demographics.constants";
import { ChevronLeft, Loader2 } from "lucide-react";

import { toSydneyYYYYMMDD } from "../utils/helpers";

export default function EditPersonPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { users, departments, fetchManagerUsers, managerUsers, updateUser, jobRoles, employmentEntities, fetchEmploymentEntities, userRoles } = useData();
  const { currentUser } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || "/people";

  const [isLoading, setIsLoading] = useState(false);
  const [vendors, setVendors] = useState<SubcontractorVendorOption[]>([]);

  const [editingUser, setEditingUser] = useState<any>(null);

  useEffect(() => {
    if (managerUsers.length === 0) fetchManagerUsers();
    if (employmentEntities.length === 0) fetchEmploymentEntities();
    
    if (id) {
      const userToEdit = (users as User[] || []).find((u) => u.id == id);
      if (userToEdit) {
        setEditingUser({
          name: userToEdit.name || "",
          email: userToEdit.email || "",
          mobile: userToEdit.mobile || "",
          jobRole: userToEdit.jobRole || "",
          department: userToEdit.department ? (typeof userToEdit.department === "object" ? userToEdit.department.id : userToEdit.department) : "",
          userRole: userToEdit.userRole || "Worker",
          manager: userToEdit.manager ? (typeof userToEdit.manager === "object" ? (userToEdit.manager as any).id : userToEdit.manager) : "none",
          dateOfBirth: toSydneyYYYYMMDD(userToEdit.dateOfBirth),
          startDate: toSydneyYYYYMMDD(userToEdit.startDate),
          showInAttendance: userToEdit.showInAttendance || false,
          employmentStatus: userToEdit.employmentStatus || "",
          employmentEntity: userToEdit.employmentEntity ? (typeof userToEdit.employmentEntity === "object" ? userToEdit.employmentEntity.id : userToEdit.employmentEntity) : null,
          gender: userToEdit.gender || "",
          indigenousStatus: userToEdit.indigenousStatus || "",
          countryOfBirth: userToEdit.countryOfBirth || "",
          disabilityStatus: userToEdit.disabilityStatus || "",
          workLocation: userToEdit.workLocation || "",
          isSubcontractor: userToEdit.isSubcontractor ?? null,
          subcontractorVendorId: userToEdit.subcontractorVendor?.id ?? userToEdit.subcontractorVendorId ?? null,
          residentialAddress: userToEdit.residentialAddress || "",
          emergencyContactName: userToEdit.emergencyContactName || "",
          emergencyContactNumber: userToEdit.emergencyContactNumber || "",
          emergencyContactRelationship: userToEdit.emergencyContactRelationship || ""
        });
      }
    }
  }, [id, users]);

  useEffect(() => {
    api
      .get<{ success: boolean; vendors: SubcontractorVendorOption[] }>('/api/users/subcontractor-vendors')
      .then((response) => setVendors(response.vendors))
      .catch(() => setVendors([]));
  }, []);

  const availableJobRoles = useMemo(() => {
    const uniqueJobRoles = new Map();
    jobRoles.forEach(role => {
      if (role.archived) return;
      const normalizedTitle = role.title.trim().toLowerCase();
      if (!uniqueJobRoles.has(normalizedTitle)) {
        uniqueJobRoles.set(normalizedTitle, {
          id: role.id,
          title: role.title,
          departmentId: role.departmentId
        });
      }
    });
    const uniqueRolesArray = Array.from(uniqueJobRoles.values());
    return uniqueRolesArray.sort((a, b) => a.title.localeCompare(b.title));
  }, [jobRoles]);

  // Values keep the raw department id — the same identifier the old Select
  // items carried — so the payload submitted on update is unchanged.
  const departmentOptions = useMemo(
    () =>
      departments.map((dept) => ({
        label: `${dept.departmentCode} - ${dept.departmentName}`,
        value: dept.id,
      })),
    [departments]
  );

  const jobRoleOptions = useMemo(
    () => availableJobRoles.map((role) => ({ label: role.title, value: role.title })),
    [availableJobRoles]
  );

  const vendorOptions = useMemo(
    () =>
      vendors.map((vendor) => ({
        label: `${vendor.tradingName || vendor.legalName}${vendor.isActive ? "" : " (Inactive)"}`,
        value: String(vendor.id),
      })),
    [vendors]
  );

  // Self is excluded from the manager list — a user cannot be their own manager.
  const managerOptions = useMemo<PersonOption[]>(
    () =>
      managerUsers
        .filter((u) => String(u.id) !== String(id))
        .map((u) => ({
          id: Number(u.id),
          name: u.name,
          email: u.email,
          jobRole: u.jobRole || null,
          department:
            u.department != null && typeof u.department === "object"
              ? { id: Number(u.department.id), departmentName: u.department.departmentName }
              : null,
        })),
    [managerUsers, id]
  );

  if (currentUser?.userRole !== "Admin" && currentUser?.userRole !== "Director" && currentUser?.userRole !== "Manager") {
    return (
      <div className="flex items-center justify-center h-full">
        <p>You don't have permission to access this page</p>
      </div>
    );
  }

  if (!editingUser) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const canChangePassword = currentUser?.userRole === "Admin" || currentUser?.userRole === "Director";

  const handleEdit = async () => {
    if (!editingUser.email || !editingUser.name || !editingUser.department || !editingUser.userRole || !editingUser.jobRole || !editingUser.startDate) {
      toast.error("Required fields must be filled");
      return;
    }

    if (editingUser.userRole !== "Admin" && (!editingUser.manager || editingUser.manager === "none")) {
      toast.error("Manager is required for non-Admin roles");
      return;
    }

    if (editingUser.isSubcontractor && !editingUser.subcontractorVendorId) {
      toast.error("Select a subcontractor vendor");
      return;
    }

    const updates: any = {
      mobile: editingUser.mobile || undefined,
      jobRole: editingUser.jobRole,
      department: editingUser.department,
      userRole: editingUser.userRole,
      name: editingUser.name,
      manager: editingUser.manager === "none" ? null : editingUser.manager,
      dateOfBirth: editingUser.dateOfBirth || undefined,
      startDate: editingUser.startDate || undefined,
      showInAttendance: editingUser.showInAttendance,
      employmentStatus: editingUser.employmentStatus || undefined,
      employmentEntity: editingUser.employmentEntity || null,
      gender: editingUser.gender || null,
      indigenousStatus: editingUser.indigenousStatus || null,
      countryOfBirth: editingUser.countryOfBirth || null,
      disabilityStatus: editingUser.disabilityStatus || null,
      workLocation: editingUser.workLocation || null,
      isSubcontractor: editingUser.isSubcontractor ?? null,
      subcontractorVendorId: editingUser.isSubcontractor ? editingUser.subcontractorVendorId : null,
      residentialAddress: editingUser.residentialAddress || undefined,
      emergencyContactName: editingUser.emergencyContactName || undefined,
      emergencyContactNumber: editingUser.emergencyContactNumber || undefined,
      emergencyContactRelationship: editingUser.emergencyContactRelationship || undefined
    };

    try {
      setIsLoading(true);
      await updateUser(id!, updates);
      await fetchManagerUsers();
      toast.success("Person updated successfully");
      navigate(from);
    } catch (error: any) {
      console.error("Error updating user:", error);
      if (error.status === 403) {
        toast.error(error.message || "You don't have permission to update this person");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate(from)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Edit Person</h1>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name*</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={editingUser.name}
                onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email*</Label>
              <Input
                id="email"
                type="email"
                placeholder="john.doe@example.com"
                value={editingUser.email}
                readOnly
                className="bg-muted cursor-not-allowed"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile</Label>
              <Input
                id="mobile"
                placeholder="04xx xxx xxx"
                value={editingUser.mobile}
                onChange={(e) => setEditingUser({ ...editingUser, mobile: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">Department*</Label>
              <Combobox
                options={departmentOptions}
                value={editingUser.department ? String(editingUser.department) : undefined}
                onChange={(value) => setEditingUser({ ...editingUser, department: value || "" })}
                placeholder="Select a department"
                searchPlaceholder="Search departments..."
                className="w-full truncate"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobRole">Job Role*</Label>
              <Combobox
                options={jobRoleOptions}
                value={editingUser.jobRole || undefined}
                onChange={(value) => {
                  const selectedRole = availableJobRoles.find(role => role.title === value);
                  if (selectedRole) {
                    setEditingUser({
                      ...editingUser,
                      jobRole: selectedRole.title,
                      department: selectedRole.departmentId
                    });
                  }
                }}
                placeholder="Select a job role"
                searchPlaceholder="Search job roles..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="userRole">User Role*</Label>
              <Select
                value={editingUser.userRole}
                onValueChange={(value) => setEditingUser({ ...editingUser, userRole: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a user role" />
                </SelectTrigger>
                <SelectContent>
                  {userRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="manager">
                Manager {editingUser.userRole !== "Admin" && <span className="text-destructive">*</span>}
              </Label>
              <PersonSelect
                id="manager"
                people={managerOptions}
                value={editingUser.manager && editingUser.manager !== "none" ? String(editingUser.manager) : ""}
                onChange={(value) => setEditingUser({ ...editingUser, manager: value || null })}
                disabled={editingUser.userRole === "Admin"}
                placeholder={editingUser.userRole === "Admin" ? "Not required for Admin" : "Select a manager"}
              />
              {editingUser.userRole !== "Admin" && (
                <p className="text-sm text-muted-foreground">Manager is required for all roles except Admin</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateOfBirth">Date of Birth</Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={editingUser.dateOfBirth || ""}
                onChange={(e) => setEditingUser({ ...editingUser, dateOfBirth: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date <span className="text-destructive">*</span></Label>
              <Input
                id="startDate"
                type="date"
                value={editingUser.startDate || ""}
                onChange={(e) => setEditingUser({ ...editingUser, startDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="employmentStatus">Status</Label>
              <Select
                value={editingUser.employmentStatus || ""}
                onValueChange={(value) => setEditingUser({ ...editingUser, employmentStatus: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employment status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Permanent FT">Permanent FT</SelectItem>
                  <SelectItem value="Permanent PT">Permanent PT</SelectItem>
                  <SelectItem value="Casual">Casual</SelectItem>
                  <SelectItem value="Maximum Term FT">Maximum Term FT</SelectItem>
                  <SelectItem value="Maximum Term PT">Maximum Term PT</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="employmentEntity">Employment Entity</Label>
              <Select
                value={editingUser.employmentEntity ? String(editingUser.employmentEntity) : ""}
                onValueChange={(value) => setEditingUser({ ...editingUser, employmentEntity: value ? parseInt(value) : null })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employment entity" />
                </SelectTrigger>
                <SelectContent>
                  {employmentEntities.map((entity) => (
                    <SelectItem key={entity.id} value={String(entity.id)}>
                      {entity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="workLocation">Work Location</Label>
              <Select
                value={editingUser.workLocation || "none"}
                onValueChange={(value) => setEditingUser({ ...editingUser, workLocation: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select work location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recorded</SelectItem>
                  {WORK_LOCATION_OPTIONS.map((location) => (
                    <SelectItem key={location} value={location}>{location}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="isSubcontractor">Subcontractor</Label>
              <Select
                value={
                  editingUser.isSubcontractor === true
                    ? "yes"
                    : editingUser.isSubcontractor === false
                      ? "no"
                      : "empty"
                }
                onValueChange={(value) =>
                  setEditingUser({
                    ...editingUser,
                    isSubcontractor:
                      value === "yes" ? true : value === "no" ? false : null,
                    subcontractorVendorId: value === "yes" ? editingUser.subcontractorVendorId : null,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select subcontractor status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="empty">Not recorded</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editingUser.isSubcontractor && (
              <div className="space-y-2">
                <Label htmlFor="subcontractorVendorId">Subcontractor Name</Label>
                <Combobox
                  options={vendorOptions}
                  value={editingUser.subcontractorVendorId ? String(editingUser.subcontractorVendorId) : undefined}
                  onChange={(value) =>
                    setEditingUser({
                      ...editingUser,
                      subcontractorVendorId: value ? parseInt(value) : null,
                    })
                  }
                  placeholder="Select a vendor"
                  searchPlaceholder="Search vendors..."
                  className="w-full truncate"
                />
                {vendors.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No vendors are available. Add one under Lists → Vendors, or check your access.
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2 md:col-span-2 border-t border-border pt-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Demographic Information
              </h3>
              <p className="text-xs text-muted-foreground">
                Optional, self-identified. Used for aggregate HR reporting only; groups under five are suppressed.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select
                value={editingUser.gender || "none"}
                onValueChange={(value) => setEditingUser({ ...editingUser, gender: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recorded</SelectItem>
                  {GENDER_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="indigenousStatus">Aboriginal and/or Torres Strait Islander</Label>
              <Select
                value={editingUser.indigenousStatus || "none"}
                onValueChange={(value) => setEditingUser({ ...editingUser, indigenousStatus: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recorded</SelectItem>
                  {INDIGENOUS_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="countryOfBirth">Country of Birth</Label>
              <Select
                value={editingUser.countryOfBirth || "none"}
                onValueChange={(value) => setEditingUser({ ...editingUser, countryOfBirth: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country of birth" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recorded</SelectItem>
                  {COUNTRY_OF_BIRTH_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="disabilityStatus">Disability Status</Label>
              <Select
                value={editingUser.disabilityStatus || "none"}
                onValueChange={(value) => setEditingUser({ ...editingUser, disabilityStatus: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select disability status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not recorded</SelectItem>
                  {DISABILITY_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="residentialAddress">Residential Address</Label>
              <Input
                id="residentialAddress"
                placeholder="123 Main St, Sydney NSW 2000"
                value={editingUser.residentialAddress}
                onChange={(e) => setEditingUser({ ...editingUser, residentialAddress: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergencyContactName">Emergency Contact Name</Label>
              <Input
                id="emergencyContactName"
                placeholder="Jane Doe"
                value={editingUser.emergencyContactName}
                onChange={(e) => setEditingUser({ ...editingUser, emergencyContactName: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergencyContactNumber">Emergency Contact Number</Label>
              <Input
                id="emergencyContactNumber"
                placeholder="04xx xxx xxx"
                value={editingUser.emergencyContactNumber}
                onChange={(e) => setEditingUser({ ...editingUser, emergencyContactNumber: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emergencyContactRelationship">Emergency Contact Relationship</Label>
              <Combobox
                options={EMERGENCY_CONTACT_RELATIONSHIP_OPTIONS}
                value={editingUser.emergencyContactRelationship || undefined}
                onChange={(value) => setEditingUser({ ...editingUser, emergencyContactRelationship: value || "" })}
                placeholder="Select relationship"
                searchPlaceholder="Search relationships..."
              />
            </div>

            <div className="space-y-4 md:col-span-2 border-t pt-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="showInAttendance"
                  checked={editingUser.showInAttendance}
                  onCheckedChange={(checked) => setEditingUser({ ...editingUser, showInAttendance: checked })}
                />
                <Label htmlFor="showInAttendance">Show in Attendance and Leave</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Enable this if the worker should appear in the Attendance and Leave modules. Recommended for full-time workers. Not recommended for casual workers.
              </p>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end gap-4 border-t px-6 py-4">
          <Button variant="outline" onClick={() => navigate(from)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleEdit} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
